"use client";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { orderActions } from "@/store/actions/order.actions";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useCallback, useEffect, useState } from "react";
import { columns } from "./columns";
import { OrderStatus, Order } from "@/types/models/order";
import {
  OrderFilters,
  OrderFilterModal,
} from "@/components/orders/OrderFilterModal";
import { useNavigate, useParams } from "react-router-dom";
import { DataView } from "@/components/common/DataView";
import { FilterConfig, ActionButton } from "@/components/common/DataViewTypes";
import { CreateOrderModal } from "@/components/modals/CreateOrderModal";
import {
  createManualOrder,
  EnhancedCreateOrderPayload,
} from "@/services/orderService";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Map UI tabs to API statuses
const orderTabs = [
  { label: "Created", status: "CREATED" as OrderStatus },
  { label: "Confirmed", status: "CONFIRMED" as OrderStatus },
  { label: "Shipping", status: "SHIPPING" as OrderStatus },
  { label: "Returning", status: "RETURNING" as OrderStatus },
  { label: "Completed", status: "COMPLETED" as OrderStatus },
  { label: "Cancelled", status: "CANCELLED" as OrderStatus },
  { label: "All", status: null },
];

const Orders = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<OrderStatus | null>("CREATED");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters, setAppliedFilters] = useState<OrderFilters>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const pageSize = 10;

  const {
    list,
    pagination,
    loading,
    error,
    searchResults,
    searchPagination,
    searchLoading,
  } = useAppSelector((state) => state.order);

  const fetchOrders = useCallback(() => {
    console.log("[Orders List] fetchOrders called with:", {
      pageIndex,
      activeTab,
      searchQuery,
      appliedFilters,
    });
    console.trace("[Orders List] Stack trace for fetchOrders");

    const hasFilters = Object.keys(appliedFilters).length > 0;
    const hasSearch = searchQuery.trim();

    if (hasSearch || hasFilters) {
      // Build search/filter parameters
      const params: any = {
        page: pageIndex + 1,
        limit: pageSize,
        sort_by: "created_at",
        sort_order: "desc",
        ...appliedFilters,
      };

      if (hasSearch) {
        params.q = searchQuery;
      }

      // If we have an active tab and no status filter, use tab status
      if (activeTab && !appliedFilters.status) {
        params.status = activeTab;
      }

      dispatch(orderActions.searchOrders(params));
    } else {
      dispatch(
        orderActions.fetchOrders({
          page: pageIndex + 1,
          limit: pageSize,
          sort_by: "created_at",
          sort_order: "desc",
          ...(activeTab && { status: activeTab }),
        }),
      );
    }
  }, [
    appliedFilters,
    searchQuery,
    pageIndex,
    activeTab,
    dispatch,
  ]);

  // Use search results when searching or filtering, otherwise use regular list
  const hasSearchOrFilters =
    searchQuery.trim() || Object.keys(appliedFilters).length > 0;
  const currentData = hasSearchOrFilters ? searchResults || [] : list || [];
  const currentPagination = hasSearchOrFilters ? searchPagination : pagination;
  const currentLoading = hasSearchOrFilters ? searchLoading : loading;

  useEffect(() => {
    // Only fetch if we don't have data OR if page/tab changed
    if (currentData.length === 0 || pageIndex > 0) {
      console.log("[Orders List] useEffect triggered - fetching orders");
      fetchOrders();
    }
  }, [
    pageIndex,
    activeTab,
    currentData.length,
    fetchOrders,
  ]);

  useEffect(() => {
    setPageIndex(0); // Reset to first page when search or filters change
  }, [searchQuery, appliedFilters]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleFilter = (filters: OrderFilters) => {
    setAppliedFilters(filters);
    setPageIndex(0);
  };

  const handleTabChange = (status: OrderStatus | null) => {
    setActiveTab(status);
    setPageIndex(0);
    // Don't clear search and filters when changing tabs
  };

  const handleRowClick = (order: Order) => {
    navigate(`/${storeType}/${storeId}/orders/${order.id}`);
  };

  // Convert setPageIndex to onPageChange
  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const getStatusTitle = () => {
    if (!activeTab) return "All Orders";
    switch (activeTab) {
      case "CREATED":
        return "Created Orders";
      case "CONFIRMED":
        return "Confirmed Orders";
      case "SHIPPING":
        return "Shipping Orders";
      case "RETURNING":
        return "Returning Orders";
      case "COMPLETED":
        return "Completed Orders";
      case "CANCELLED":
        return "Cancelled Orders";
      default:
        return "Orders";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab) return "Manage and track all your orders in one place";
    switch (activeTab) {
      case "CREATED":
        return "Recently created orders awaiting confirmation";
      case "CONFIRMED":
        return "Orders confirmed and ready for processing";
      case "SHIPPING":
        return "Orders currently in transit";
      case "RETURNING":
        return "Orders being returned";
      case "COMPLETED":
        return "Successfully completed orders";
      case "CANCELLED":
        return "Orders that have been cancelled";
      default:
        return "Manage and track your orders";
    }
  };

  const handleFilterApply = (filters: OrderFilters) => {
    setAppliedFilters(filters);
    if (handleFilter) {
      handleFilter(filters);
    }
  };

  const getActiveFilterCount = () => {
    return Object.values(appliedFilters).filter(
      (value) => value !== "" && value !== undefined && value !== null,
    ).length;
  };

  // Handle create order
  const handleCreateOrder = async (orderData: EnhancedCreateOrderPayload) => {
    try {
      setIsCreatingOrder(true);
      const response = await createManualOrder(orderData);

      if (response.success) {
        toast.success("Order created successfully!");
        setIsCreateOrderModalOpen(false);
        // Refresh orders list
        fetchOrders();
      } else {
        toast.error(response.message || "Failed to create order");
      }
    } catch (error: any) {
      console.error("Error creating order:", error);
      toast.error(error?.message || "Failed to create order");
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Filter configuration
  const filterConfig: FilterConfig = {
    component: (
      <OrderFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        initialFilters={appliedFilters}
      />
    ),
    isOpen: isFilterModalOpen,
    onToggle: () => setIsFilterModalOpen(!isFilterModalOpen),
    activeCount: getActiveFilterCount(),
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    const hasFilters = Object.keys(appliedFilters).length > 0;
    const hasSearch = searchQuery.trim();

    if (hasSearch || hasFilters) {
      const params: any = {
        page: pageIndex + 1,
        limit: pageSize,
        sort_by: "created_at",
        sort_order: "desc",
        ...appliedFilters,
      };
      if (hasSearch) {
        params.q = searchQuery;
      }
      if (activeTab && !appliedFilters.status) {
        params.status = activeTab;
      }
      dispatch(orderActions.searchOrders(params));
    } else {
      dispatch(
        orderActions.fetchOrders({
          page: pageIndex + 1,
          limit: pageSize,
          sort_by: "created_at",
          sort_order: "desc",
          ...(activeTab && { status: activeTab }),
        }),
      );
    }
  };

  // Action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: (
        <RefreshCw
          className={`h-4 w-4 ${currentLoading ? "animate-spin" : ""}`}
        />
      ),
      onClick: handleRefresh,
      variant: "outline",
      disabled: currentLoading,
    },
    {
      label: "Create Order",
      icon: <Plus className="h-4 w-4" />,
      onClick: () => setIsCreateOrderModalOpen(true),
      variant: "primary",
      disabled: isCreatingOrder,
    },
  ];

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={orderTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.status),
              isActive: activeTab === tab.status,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={currentData}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading}
            error={error}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search orders..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            filterConfig={filterConfig}
            actionButtons={actionButtons}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="orders"
            loadingMessage="Loading..."
          />
        </div>
      </TableContainer>

      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onCreate={handleCreateOrder}
      />
    </div>
  );
};

export default Orders;
